import { Trans, useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { Button } from "@offstreetparking/design-system/forms";
import { Heading, Link, Text } from "@offstreetparking/design-system/typography";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { usePortalFlowHost } from "../../../../host";
import { PRIVACY_URL, TERMS_URL } from "../../../../lib/legal-urls";
import { ProrationInfoBanner } from "../../../dataDisplay/parkingDetails";
import { PaymentErrorAlert } from "../../../forms/Alert";
import { usePermitFormActions, usePermitFormData } from "../../PermitContextProvider";
import { usePaymentRetryError, usePermitWizardSettings } from "../../PermitContextProvider/PermitContextProvider";
import type { PermitFormData, PermitPostResultData } from "../../PermitContextProvider/types";
import { useSavedPermits } from "../../hooks/useSavedPermits";
import type { PermitTypeDto } from "../../types";
import { InvoiceDetails } from "./components/InvoiceDetails";
import { type GatewayPaymentError, PaymentFormError } from "./types";
import { usePaymentSession } from "./usePaymentSession";
export type PaymentFormProps = {
	permitType: PermitTypeDto;
	locationLookupId?: string;
	tenantId?: number;
	time: {
		startDateTime: string;
		endDateTime: string;
		timezone: string;
	};
	invoice?: {
		total: number;
		subtotal: number;
		taxAndFees: {
			description: string;
			longDescription?: string;
			percent?: number;
			fixedAmount?: number;
			total: number;
		}[];
		discountItems?: {
			description: string;
			total: number;
			type?: "Proration";
		}[];
		fixedRateItems?: {
			style: string;
			amount: number;
			units: number;
			total: number;
		}[];
	};
	email?: string;
	vehicles?: {
		plate: string;
		state: string;
	}[];
	currency: string;
	locale: string;
	formId: string;
	onSubmit: (data: PermitPostResultData) => void;
	setDuplicatedPermit: ({
		plate,
		permitIntervals,
		publicIdentifier,
	}: {
		plate: string;
		permitIntervals: PermitFormData["permitIntervals"];
		publicIdentifier: string;
	}) => void;
};
export const PaymentForm = ({
	permitType,
	locationLookupId,
	tenantId,
	invoice,
	time,
	email,
	vehicles,
	locale,
	formId,
	onSubmit,
	setDuplicatedPermit,
}: PaymentFormProps) => {
	const { addPermit } = useSavedPermits();
	if (!time.startDateTime || !time.endDateTime) {
		return;
	}
	const { t } = useLingui();
	const { setIsProcessingPermit, setPaymentRetryError } = usePermitFormActions();
	const wizardSettings = usePermitWizardSettings();
	const permitFormData = usePermitFormData();
	const selectedChoice =
		wizardSettings.choices.find((choice) => choice.permitTypeId === permitFormData.parkingChoiceId) ??
		wizardSettings.choices[0];
	const [{ paymentError, displayError }, setPaymentError] = useState<{
		paymentError?: GatewayPaymentError;
		displayError: boolean;
	}>({
		displayError: false,
	});
	const { paymentSession, isCreating, restart } = usePaymentSession({ tenantId, locale, setDuplicatedPermit });
	const retryError = usePaymentRetryError();
	const { PaymentGatewayPanel: GatewayPanel } = usePortalFlowHost();
	const handlePaymentSuccess = useCallback(
		(publicIdentifier: string) => {
			if (locationLookupId) {
				addPermit(locationLookupId, {
					publicIdentifier,
					publicAccessToken: paymentSession?.permit?.publicAccessToken ?? "",
				});
			}
			onSubmit({
				publicIdentifier,
				publicAccessToken: paymentSession?.permit?.publicAccessToken ?? undefined,
			});
		},
		[addPermit, locationLookupId, onSubmit, paymentSession],
	);

	const tryAgainButtonRef = useRef<HTMLButtonElement>(null);
	const retryMessageId = useId();
	useEffect(() => {
		if (retryError && !isCreating) {
			tryAgainButtonRef.current?.focus();
		}
	}, [retryError, isCreating]);

	const tryAgain = () => {
		setPaymentError({ displayError: false });
		restart();
	};

	return (
		<div className="space-y-4">
			<Heading>{t`Enter Payment Information`}</Heading>
			{invoice?.discountItems?.some((item) => item.type === "Proration") && (
				<ProrationInfoBanner
					fullTermIntervals={selectedChoice?.context?.fixedIntervals}
					timezone={selectedChoice?.time.timezone}
				/>
			)}
			<InvoiceDetails
				permitType={permitType}
				invoice={invoice}
				vehicles={vehicles}
				email={email}
				time={time}
				paymentSchedule={selectedChoice?.paymentSchedule}
			/>

			{retryError ? (
				<div className="space-y-3">
					{retryError === PaymentFormError.DECLINED ? (
						<Alert
							type="error"
							title={t`Card declined`}
							message={
								<span id={retryMessageId}>
									{t`Your payment didn't complete. Re-enter your card details and try again.`}
								</span>
							}
						/>
					) : (
						<Alert
							type="error"
							title={t({
								comment:
									"Title of the payment step error shown when the card payment may or may not have gone through",
								message: "We couldn't confirm your payment",
							})}
							message={
								<span id={retryMessageId}>
									{t({
										comment:
											"Body of the payment step error shown when the card payment may or may not have gone through; a Try again button follows it",
										message:
											"Check your email for a receipt before trying again. If you didn't get one, try again.",
									})}
								</span>
							}
						/>
					)}
					<Button
						ref={tryAgainButtonRef}
						isLoading={isCreating}
						onClick={tryAgain}
						aria-describedby={retryMessageId}
					>
						{t({
							comment:
								"Button that starts a new payment after a declined card, or after an unconfirmed payment the parker found no receipt for",
							message: "Try again",
						})}
					</Button>
				</div>
			) : (
				displayError && <PaymentErrorAlert error={paymentError} />
			)}

			{!retryError && GatewayPanel && (
				<GatewayPanel
					paymentSession={paymentSession}
					formId={formId}
					locationLookupId={locationLookupId}
					onSuccess={handlePaymentSuccess}
					onRetryableError={(error) => {
						setPaymentRetryError(error);
						setIsProcessingPermit(false);
					}}
					onPaymentError={(error) => {
						setPaymentError({
							paymentError: error,
							displayError: true,
						});
						setIsProcessingPermit(false);
						if (!error) {
							restart();
						}
					}}
					setIsProcessing={(isProcessing) => setIsProcessingPermit(isProcessing)}
				/>
			)}
			{!retryError && !GatewayPanel && <PaymentErrorAlert />}
			<Text className="my-4">
				<Trans>
					By continuing, I accept Offstreet’s{" "}
					<Link href={TERMS_URL} isExternal>
						Terms of Service
					</Link>{" "}
					and{" "}
					<Link href={PRIVACY_URL} isExternal>
						Privacy Policy
					</Link>
					.
				</Trans>
			</Text>
		</div>
	);
};
